import { useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, Clipboard, Globe, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CompetitorsInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}

export const cleanDomain = (raw: string): string => {
  let d = String(raw).trim().toLowerCase();
  d = d
    .replace(/^https?:\/\//, '')
    .replace(/^www\./, '')
    .replace(/\/.*$/, '')
    .replace(/\s.*$/, '');
  return d;
};

export const isValidDomain = (d: string): boolean => /^[a-z0-9][a-z0-9-]*(?:\.[a-z0-9-]+)+$/.test(d);

function Favicon({ domain }: { domain: string }) {
  const [err, setErr] = useState(false);
  if (err || !domain) {
    return (
      <span className="grid place-items-center w-3.5 h-3.5 rounded-sm bg-warm-bg text-muted-foreground">
        <Globe className="size-2.5" />
      </span>
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?sz=32&domain=${domain}`}
      onError={() => setErr(true)}
      width={14}
      height={14}
      alt=""
      className="rounded-sm block"
    />
  );
}

export function CompetitorsInput({
  value = [],
  onChange,
  placeholder = 'competitor.com — paste a list to bulk-add',
}: CompetitorsInputProps) {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const add = (raw: string) => {
    const d = cleanDomain(raw);
    if (!d) return;
    if (!isValidDomain(d)) {
      setError(`"${d}" doesn't look like a valid domain`);
      return;
    }
    if (value.includes(d)) {
      setError(`${d} is already in the list`);
      return;
    }
    setError('');
    onChange([...value, d]);
    setDraft('');
  };
  const remove = (i: number) => {
    const n = [...value];
    n.splice(i, 1);
    onChange(n);
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (draft.trim()) add(draft);
    } else if (e.key === 'Backspace' && !draft && value.length) {
      remove(value.length - 1);
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text');
    if (text.includes('\n') || text.includes(',')) {
      e.preventDefault();
      const lines = text.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      if (lines.length > 1) {
        setBulkText(text);
        setBulkOpen(true);
      } else {
        add(lines[0] || text);
      }
    }
  };

  const handleBulkAdd = () => {
    const lines = bulkText.split(/[\n,]/).map((s) => cleanDomain(s)).filter(Boolean);
    const next = [...value];
    let added = 0;
    lines.forEach((d) => {
      if (!isValidDomain(d)) return;
      if (!next.includes(d)) {
        next.push(d);
        added++;
      }
    });
    onChange(next);
    setBulkOpen(false);
    setBulkText('');
    setError(added ? '' : 'No valid new domains were found');
  };

  const bulkPreview = useMemo(() => {
    if (!bulkText.trim()) return [];
    return bulkText
      .split(/[\n,]/)
      .map((s) => {
        const d = cleanDomain(s);
        if (!d) return null;
        const valid = isValidDomain(d);
        const dup = value.includes(d);
        return { d, valid, dup };
      })
      .filter((x): x is { d: string; valid: boolean; dup: boolean } => Boolean(x));
  }, [bulkText, value]);

  const validNewCount = bulkPreview.filter((b) => b.valid && !b.dup).length;

  return (
    <div>
      <div
        onClick={() => inputRef.current?.focus()}
        className={cn(
          'flex flex-wrap gap-1.5 items-center px-2 py-1.5 min-h-[42px] rounded-lg bg-card cursor-text',
          'transition-[border-color,box-shadow] duration-150 border',
          focused ? 'border-coral ring-[3px] ring-coral/15' : 'border-border',
        )}
      >
        {value.map((v, i) => (
          <span
            key={v + i}
            className="inline-flex items-center gap-1.5 bg-warm-bg text-foreground border border-border pl-2 pr-1 py-0.5 rounded-md text-[12.5px] font-medium"
          >
            <Favicon domain={v} />
            <span className="font-mono text-[12px]">{v}</span>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                remove(i);
              }}
              className="grid place-items-center w-4 h-4 rounded bg-transparent border-none text-muted-foreground hover:text-foreground cursor-pointer"
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError('');
          }}
          onKeyDown={onKey}
          onPaste={onPaste}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            if (draft.trim()) add(draft);
          }}
          placeholder={value.length === 0 ? placeholder : ''}
          className={cn(
            'flex-1 min-w-[180px] border-none outline-none px-1 py-1 text-[13.5px] bg-transparent',
            value.length > 0 && 'font-mono',
          )}
        />
      </div>

      <div className="flex items-center justify-between mt-1.5 gap-2.5">
        <div className="flex-1 min-w-0">
          {error ? (
            <div className="text-[11.5px] text-amber-700 dark:text-amber-400 flex items-center gap-1">
              <AlertTriangle className="size-3" />
              {error}
            </div>
          ) : (
            <div className="text-[11px] text-muted-foreground">
              {value.length} never-link domain{value.length !== 1 ? 's' : ''} · paste a list (one per line or comma-separated) to bulk-add
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setBulkOpen(true)}
          className="inline-flex items-center gap-1 text-[11.5px] px-2.5 py-1 rounded-md bg-transparent border border-border text-muted-foreground hover:text-foreground cursor-pointer font-medium"
        >
          <Clipboard className="size-3" /> Bulk paste
        </button>
      </div>

      {bulkOpen && (
        <div
          onClick={() => setBulkOpen(false)}
          className="fixed inset-0 bg-foreground/30 backdrop-blur-sm grid place-items-center z-[80]"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-[520px] max-w-[92vw] max-h-[82vh] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="px-4 pt-4 pb-3 border-b border-border">
              <div className="text-sm font-bold">Bulk add competitors</div>
              <div className="text-[12px] text-muted-foreground mt-0.5">
                Paste a list — one per line, or comma-separated. We'll clean URLs and skip duplicates.
              </div>
            </div>
            <div className="p-3.5 grid grid-cols-2 gap-3 overflow-auto">
              <div>
                <div className="text-[11px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">Paste here</div>
                <textarea
                  autoFocus
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  placeholder={'mrrooter.com\nrotorooter.com\nhttps://www.bensfranklinplumbing.com'}
                  rows={10}
                  className="w-full px-3 py-2.5 text-[13px] font-mono border border-border rounded-lg outline-none bg-card resize-y min-h-[200px] focus:border-coral focus:ring-[3px] focus:ring-coral/15"
                />
              </div>
              <div>
                <div className="text-[11px] font-semibold text-muted-foreground mb-1.5 uppercase tracking-wider">
                  Preview ({validNewCount} new)
                </div>
                <div className="border border-border rounded-lg p-2 min-h-[200px] max-h-[260px] overflow-auto bg-warm-bg flex flex-col gap-1">
                  {bulkPreview.length === 0 && (
                    <div className="text-[12px] text-muted-foreground p-2">Paste to preview…</div>
                  )}
                  {bulkPreview.map((b, i) => (
                    <div
                      key={i}
                      className={cn(
                        'flex items-center gap-1.5 px-2 py-1 rounded',
                        b.valid && !b.dup
                          ? 'bg-card border border-border opacity-100'
                          : 'bg-transparent border border-transparent opacity-55',
                      )}
                    >
                      <Favicon domain={b.d} />
                      <span className="font-mono text-[12px] flex-1">{b.d}</span>
                      {!b.valid && <span className="text-[10px] text-amber-700 dark:text-amber-400">invalid</span>}
                      {b.valid && b.dup && <span className="text-[10px] text-muted-foreground">already added</span>}
                      {b.valid && !b.dup && <Check className="size-3 text-[hsl(var(--success))]" />}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-4 py-3 border-t border-border flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setBulkOpen(false)}
                className="px-3.5 py-2 rounded-lg text-[13px] font-medium bg-card border border-border text-foreground hover:bg-warm-bg cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleBulkAdd}
                disabled={validNewCount === 0}
                className={cn(
                  'px-3.5 py-2 rounded-lg text-[13px] font-semibold border text-white cursor-pointer',
                  validNewCount === 0
                    ? 'bg-coral/50 border-coral/50 cursor-not-allowed'
                    : 'bg-coral hover:bg-coral-hover border-coral',
                )}
              >
                Add {validNewCount} new
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
